import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useRef } from 'react';
import { UXPContext } from '@core/context/Context';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import { getFontAwesomeIcon } from '@utils';
/**
 * @export
 * WizardStepIndicator - A horizontal step indicator for multi-step wizards
 *
 * Displays numbered steps in a horizontal scrollable container with:
 * - Numbered circles for each step
 * - Connector lines between steps
 * - Visual states: active, completed, error, pending
 * - Smooth auto-scroll to keep active step centered
 * - Theme-aware styling
 *
 * @example
 * ```tsx
 * <WizardStepIndicator
 *   steps={[
 *     { id: 'step1', label: 'Upload File', hasError: false },
 *     { id: 'step2', label: 'Map Fields', hasError: false },
 *     { id: 'step3', label: 'Review', hasError: false }
 *   ]}
 *   currentStepId="step2"
 *   completedSteps={['step1']}
 *   onStepClick={(stepId) => navigateToStep(stepId)}
 * />
 * ```
 */
export const WizardStepIndicator = ({ steps, currentStepId, onStepClick, completedSteps = [], disableAutoScroll = false }) => {
    const context = useContext(UXPContext);
    const activeStepRef = useRef(null);
    const containerRef = useRef(null);
    // Get step index for display (1-indexed)
    const getStepNumber = (stepId) => {
        return steps.findIndex(s => s.id === stepId) + 1;
    };
    // Check if step is completed
    const isCompleted = (stepId) => {
        return completedSteps.includes(stepId);
    };
    // Check if step is active
    const isActive = (stepId) => {
        return stepId === currentStepId;
    };
    // Check if step is before the active step (for connector styling)
    const isBeforeActive = (stepId) => {
        const currentIndex = steps.findIndex(s => s.id === currentStepId);
        const stepIndex = steps.findIndex(s => s.id === stepId);
        return stepIndex < currentIndex;
    };
    // Smooth scroll to center the active step (only if not disabled)
    useEffect(() => {
        if (!disableAutoScroll && activeStepRef.current && containerRef.current) {
            activeStepRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [currentStepId, disableAutoScroll]);
    // Handle step click
    const handleStepClick = (stepId) => {
        if (onStepClick && !isActive(stepId)) {
            onStepClick(stepId);
        }
    };
    return (_jsx("div", { className: "uxpcore_wizardstep__container", ref: containerRef, children: _jsx("div", { className: "uxpcore_wizardstep__scroll-container", children: steps.map((step, index) => {
                const stepNumber = getStepNumber(step.id);
                const active = isActive(step.id);
                const completed = isCompleted(step.id);
                const hasError = step.hasError;
                const clickable = !!onStepClick;
                const showConnector = index < steps.length - 1;
                const connectorActive = isBeforeActive(step.id) || active;
                return (_jsxs(React.Fragment, { children: [_jsxs("div", { ref: active ? activeStepRef : null, className: `uxpcore_wizardstep__step ${active ? 'uxpcore_wizardstep__step--active' : ''} ${completed ? 'uxpcore_wizardstep__step--completed' : ''} ${hasError ? 'uxpcore_wizardstep__step--error' : ''} ${clickable ? 'uxpcore_wizardstep__step--clickable' : ''}`, onClick: () => handleStepClick(step.id), role: "button", tabIndex: clickable ? 0 : -1, "aria-current": active ? 'step' : undefined, "aria-label": `${context.$L('uxp-core.text.step')} ${stepNumber}: ${step.label}`, children: [_jsx("div", { className: "uxpcore_wizardstep__step-circle", children: completed ? (_jsx(FontAwesomeIconWrapper, { icon: getFontAwesomeIcon('fas check') })) : (_jsx("span", { children: stepNumber })) }), _jsx("div", { className: "uxpcore_wizardstep__step-label", children: step.label })] }), showConnector && (_jsx("div", { className: `uxpcore_wizardstep__connector ${connectorActive ? 'uxpcore_wizardstep__connector--active' : ''}` }))] }, step.id));
            }) }) }));
};
